import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useCallback } from 'react';
import { QueryKey, UseQueryOptions } from 'react-query';
import type { Paginated, QuotationSupplier, QuotationSupplierProduct } from './types';
import { restaurantInquiriesEndpoint } from './use-create-restaurant-inquiry-api';

// GET /cms/restaurant-inquiries/quotation-suppliers — searchable list of
// suppliers eligible to be quoted (backed by supplier_profiles). This is step 1
// of the create-quote flow; step 2 is .../quotation-suppliers/:supplierId/products.
// Exported so product-create flows can invalidate the catalog caches by prefix.
export const quotationSuppliersEndpoint = `${restaurantInquiriesEndpoint}/quotation-suppliers`;

export interface QuotationSupplierOption {
  value: number; // supplierId (NOT the profile id)
  label: string;
  raw: QuotationSupplier;
}

export function useSearchQuotationSuppliersApi() {
  const loadOptions = useCallback((inputValue: string, callback: (options: QuotationSupplierOption[]) => void) => {
    http
      .get(quotationSuppliersEndpoint, {
        params: { page: 1, limit: 20, search: inputValue || undefined },
      })
      .then(({ data }) => {
        const list: QuotationSupplier[] = data?.data ?? [];
        callback(
          list.map((s) => ({
            value: s.supplierId,
            label: s.supplierName || s.supplier?.name || `Supplier #${s.supplierId}`,
            raw: s,
          }))
        );
      })
      .catch(() => callback([]));
  }, []);

  return { loadOptions };
}

export interface QuotationSupplierProductOption {
  value: number; // productId for POST .../quotes
  label: string;
  raw: QuotationSupplierProduct;
}

// Stable partition: ACTIVE-deal products first, original (name) order kept
// within each group. The BE orders the endpoint this way too — this per-page
// mirror keeps the dropdown deal-first against BEs that predate that order.
export function sortActiveDealsFirst(products: QuotationSupplierProduct[]): QuotationSupplierProduct[] {
  const hasActiveDeal = (p: QuotationSupplierProduct) =>
    !!p.exclusiveDeals?.length && p.exclusiveDeals[0]?.isActive !== false;
  return [...products.filter(hasActiveDeal), ...products.filter((p) => !hasActiveDeal(p))];
}

// Server-side search (iLike on nameEn/nameAr) over ONE supplier's public
// catalog, for the ProductMatcher's async select. Dependent-dropdown pattern:
// loadOptions(supplierId) returns the (inputValue, callback) function.
export function useSearchQuotationSupplierProductsApi() {
  const loadOptions = useCallback(
    (supplierId: number) => (inputValue: string, callback: (options: QuotationSupplierProductOption[]) => void) => {
      http
        .get(`${quotationSuppliersEndpoint}/${supplierId}/products`, {
          params: { page: 1, limit: 20, search: inputValue || undefined },
        })
        .then(({ data }) => {
          const list: QuotationSupplierProduct[] = data?.data ?? [];
          callback(
            sortActiveDealsFirst(list).map((p) => ({ value: p.id, label: p.nameEn || `Product #${p.id}`, raw: p }))
          );
        })
        .catch(() => callback([]));
    },
    []
  );

  return { loadOptions };
}

// GET .../quotation-suppliers/:supplierId/products — that supplier's public
// catalog (full catalog once UMS PR #2189 deploys; deal-backed rows carry
// exclusiveDeals[].isActive so the UI can flag inactive deals). The `id` of a
// row is exactly the productId for POST .../quotes. 404 if supplier unknown.
type TData = Paginated<QuotationSupplierProduct>;
type TError = AxiosError;
type Options = Omit<UseQueryOptions<TData, TError, TData, QueryKey>, 'queryKey' | 'queryFn'>;

export function useGetQuotationSupplierProductsApi(
  supplierId: number | null | undefined,
  search?: string,
  options?: Options
) {
  return useQueryWrapper<TData, TError>(
    [quotationSuppliersEndpoint, supplierId, 'products', { search }],
    async () => {
      const response = await http.get(`${quotationSuppliersEndpoint}/${supplierId}/products`, {
        params: { page: 1, limit: 50, search: search || undefined },
      });
      return response.data;
    },
    { enabled: !!supplierId, keepPreviousData: true, ...options }
  );
}
